import {
  type Api,
  canSaveToPenielly,
  LinkError,
  type LinkedProduct,
  type LinkStatus,
  Links,
  type LinksOverview,
  type MediaRef,
} from "@rhema/client";
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { useApi, useClient } from "../session/session";

// One Links object per API, and one shared copy of the link overview, so every screen agrees on
// what is linked without asking the server again.

type Snapshot = { overview: LinksOverview | null; error: string | null };

type Entry = {
  links: Links;
  snapshot: Snapshot;
  loading: Promise<void> | null;
  listeners: Set<() => void>;
};

const entries = new WeakMap<Api, Entry>();

function entryFor(api: Api): Entry {
  let entry = entries.get(api);
  if (!entry) {
    entry = {
      links: new Links(api),
      snapshot: { overview: null, error: null },
      loading: null,
      listeners: new Set(),
    };
    entries.set(api, entry);
  }
  return entry;
}

function load(entry: Entry): Promise<void> {
  entry.loading ??= entry.links.api
    .overview()
    .then((overview): Snapshot => ({ overview, error: null }))
    .catch((err: Error): Snapshot => {
      // A server without connected apps answers 404. Treat it as switched off.
      if (err instanceof LinkError && (err.code === "not_found" || err.code === "links_disabled"))
        return { overview: { enabled: false, products: [] }, error: null };
      return { overview: entry.snapshot.overview, error: err.message };
    })
    .then((snapshot) => {
      entry.snapshot = snapshot;
      entry.loading = null;
      for (const l of entry.listeners) l();
    });
  return entry.loading;
}

export function useLinks(): Links {
  return entryFor(useApi()).links;
}

/** The link overview, fetched once and shared. `reload` asks the server again. */
export function useLinkOverview() {
  const entry = entryFor(useApi());
  const subscribe = useCallback(
    (l: () => void) => {
      entry.listeners.add(l);
      return () => entry.listeners.delete(l);
    },
    [entry],
  );
  const snapshot = useSyncExternalStore(
    subscribe,
    () => entry.snapshot,
    () => entry.snapshot,
  );
  useEffect(() => {
    if (!entry.snapshot.overview) void load(entry);
  }, [entry]);
  const reload = useCallback(() => load(entry), [entry]);
  return { ...snapshot, reload, links: entry.links };
}

/** One app's status, or null while loading or when connected apps are off. */
export function useLinkStatus(product: LinkedProduct) {
  const { overview, reload, error } = useLinkOverview();
  const status: LinkStatus | null = overview?.enabled
    ? (overview.products.find((p) => p.product === product) ?? null)
    : null;
  return { status, enabled: overview ? overview.enabled : null, reload, error };
}

/** The design system's mark for each app. Malakh's mark is keyed "malak" in the UI package. */
export const MARK: Record<LinkedProduct, "queravel" | "malak" | "penielly"> = {
  queravel: "queravel",
  malakh: "malak",
  penielly: "penielly",
};

export type SaveTarget = { media: MediaRef; local?: Blob; capturedAt?: string };

/**
 * Saves received photos and videos to Penielly. Each one is decrypted on this device and goes from
 * here straight to Penielly's storage. Several go three at a time. `chatId` names the chat they
 * come from, so the server can refuse where an admin turned Penielly off.
 */
export function useSaveToPenielly(chatId?: string) {
  const client = useClient();
  const all = useLinks();
  const links = useMemo(() => (chatId ? all.inChat(chatId) : all), [all, chatId]);
  const [busy, setBusy] = useState(false);

  const save = useCallback(
    async (targets: SaveTarget[]): Promise<string> => {
      const usable = targets.filter((t) => canSaveToPenielly(t.media.mime));
      if (!usable.length) return "Penielly only takes photos and videos.";
      setBusy(true);
      try {
        const results = await links.penielly.saveMany(
          usable.map((t) => async () => ({
            blob: t.local ?? (await client.media.download(t.media)),
            mime: t.media.mime,
            ...(t.media.name ? { name: t.media.name } : {}),
            ...(t.media.width ? { width: t.media.width } : {}),
            ...(t.media.height ? { height: t.media.height } : {}),
            ...(t.capturedAt ? { capturedAt: t.capturedAt } : {}),
          })),
        );
        const failed = results.filter((r): r is PromiseRejectedResult => r.status === "rejected");
        const first = failed[0];
        if (first) {
          const err = first.reason;
          if (err instanceof LinkError && err.code === "not_linked")
            return "Link Penielly in Settings to save photos there.";
          if (failed.length === results.length)
            return (err as Error)?.message || "Saving to Penielly did not work.";
          return `Saved ${results.length - failed.length} of ${results.length} to Penielly.`;
        }
        return results.length === 1 ? "Saved to Penielly." : `Saved ${results.length} to Penielly.`;
      } finally {
        setBusy(false);
      }
    },
    [client, links],
  );

  return { save, busy };
}
